<template>
  <div class="recording-container">
    <!-- ローディングモーダル -->
    <LoadingModal :visible="isLoading" />
    
    <div class="header">
      <!--<h1>📚 録音・提出</h1>-->
      <p class="subtitle">FBを確認し、練習を始めましょう！</p>
    </div>

    <!-- アップロード進捗ポップアップ（開発用） -->
    <div v-if="ENABLE_UPLOAD_PROGRESS && showUploadProgress" class="upload-progress-popup">
      <div class="upload-progress-content">
        <h3>📤 アップロード進捗（開発用）</h3>
        <div class="progress-info">
          <p>チャンク数: {{ uploadProgress.total }}個</p>
          <p>完了: {{ uploadProgress.completed }}個</p>
          <p>進捗: {{ uploadProgress.total > 0 ? Math.round((uploadProgress.completed / uploadProgress.total) * 100) : 0 }}%</p>
        </div>
        <div class="progress-bar">
          <div 
            class="progress-fill" 
            :style="{ width: uploadProgress.total > 0 ? (uploadProgress.completed / uploadProgress.total) * 100 + '%' : '0%' }"
          ></div>
        </div>
        <button @click="showUploadProgress = false" class="btn-close">閉じる</button>
      </div>
    </div>

    <!-- 本番用アップロード進捗表示 -->
    <div v-if="showProductionUploadProgress" class="production-upload-progress-overlay">
      <div class="production-upload-progress-modal">
        <div class="upload-message">
          <h3>📤 データ送信中...</h3>
          <p>このままお待ちください。</p>
        </div>
        
        <div class="progress-container">
          <div class="progress-bar">
            <div 
              class="progress-fill" 
              :style="{ width: productionUploadProgress.percentage + '%' }"
            ></div>
          </div>
          <div class="progress-text">
            {{ productionUploadProgress.completed }} / {{ productionUploadProgress.total }}
          </div>
        </div>
        
        <div v-if="uploadStatus === 'completed'" class="completion-message">
          ✅ 送信完了！
        </div>
      </div>
    </div>




     <!-- 180日分の進捗一覧 -->
     <div class="progress-list">
       <h2>📅 学習進捗（180日）</h2>
      <div class="days-container">
        <div 
          v-for="day in 180" 
          :key="day" 
          :id="`day-${day}`"
          class="day-item"
          :class="{ 
            'current-recording': currentRecordingDay === day,
            'completed': isDayCompleted(day)
          }"
        >
          <div class="day-header">
            <div class="day-title-section">
              <h3 class="day-title">Day {{ day }}</h3>
              <button 
                @click="checkFeedback(day, 'practice', false, selectedLearnerId)" 
                class="btn-feedback-check"
                :disabled="isCheckingFeedback"
              >
                {{ isCheckingFeedback ? '確認中...' : '📝 FBを確認' }}
              </button>
            </div>
            <div class="submission-status">
              <div class="status-item practice-status">
                <span class="status-label">練習：</span>
                <span class="status-value" :class="getPracticeStatusClass(day)">
                  {{ getPracticeStatusText(day) }}
                </span>
              </div>
              <div class="status-item assignment-status">
                <span class="status-label">課題：</span>
                <span class="status-value" :class="getAssignmentStatusClass(day)">
                  {{ getAssignmentStatusText(day) }}
                </span>
              </div>
            </div>
          </div>
          
          <!-- 練習録音セクション -->
          <div class="recording-section" :class="{ 'completed': isPracticeCompleted(day) }">
            <h4 class="section-title">
              <span v-if="!isPracticeCompleted(day)">🎯 練習（15分）</span>
              <span v-else class="completed-title">✅ 練習録音完了</span>
            </h4>
            
            <!-- 録音中の表示（練習） -->
            <div 
              v-if="isRecording && currentRecordingDay === day && currentRecordingType === 'practice'"
              class="recording-status-active"
            >
              <div class="recording-indicator">
                <h4>🎤 練習録音中...</h4>
                <div class="recording-time-display">
                  <span class="time-display">{{ formatTime(displayTime) }}</span>
                </div>
                <button 
                  @click="stopRecording" 
                  class="btn btn-stop-recording"
                  :disabled="!canStopRecording"
                  :class="{ 'btn-disabled': !canStopRecording }"
                >
                  ⏹️ 録音停止
                </button>
              </div>
            </div>
            
            <!-- 提出不可（サーバー can_submit_today） -->
            <div v-if="membershipStatusLoaded && !membershipCanSubmitToday" class="holiday-pill" role="status">
              {{ membershipMessage || '今日は録音・提出はできません。' }}
            </div>
            
            <div class="recording-buttons">
              <button
                v-if="!isRecording"
                @click="startPracticeRecording(day)"
                class="btn btn-record-practice"
                :disabled="isRecordingStartDisabled"
                :title="isRecordingStartDisabled ? (recordingStartBlockedTitle) : '練習を開始します'"
              >
                🎤 練習開始ボタン
              </button>
            </div>
          </div>
          
          <!-- 課題録音セクション -->
          <div class="recording-section" :class="{ 'completed': isAssignmentCompleted(day) }">
            <h4 class="section-title">
              <span v-if="!isAssignmentCompleted(day)">📝 課題（1分）</span>
              <span v-else class="completed-title">✅ 課題録音完了</span>
            </h4>
            
            <!-- 録音中の表示（課題） -->
            <div 
              v-if="isRecording && currentRecordingDay === day && currentRecordingType === 'assignment'"
              class="recording-status-active"
            >
              <div class="recording-indicator">
                <h4>🎤 課題録音中...</h4>
                <div class="recording-time-display">
                  <span class="time-display">{{ formatTime(displayTime) }}</span>
                </div>
                <button 
                  @click="stopRecording" 
                  class="btn btn-stop-recording"
                  :disabled="!canStopRecording"
                  :class="{ 'btn-disabled': !canStopRecording }"
                >
                  ⏹️ 録音停止
                </button>
              </div>
            </div>
            
            <div class="recording-buttons">
              <button
                v-if="!isRecording"
                @click="startAssignmentRecording(day)"
                class="btn btn-record-assignment"
                :disabled="isRecordingStartDisabled"
                :title="isRecordingStartDisabled ? (recordingStartBlockedTitle) : '課題を開始します'"
              >
                🎤 課題開始ボタン
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- フィードバック確認モーダル -->
    <div v-if="showFeedbackModal" class="feedback-modal-overlay">
      <div class="feedback-modal">
        <div class="modal-header">
          <h3>📝 Day{{ currentFeedbackDay }} フィードバック</h3>
          <button @click="closeFeedbackModal" class="btn-close">×</button>
        </div>
        
        <div class="modal-content">
          <!-- 課題内容表示 -->
          <div class="assignment-section">
            <h4>📖 課題内容</h4>
            <div class="assignment-text">
              {{ feedbackData.assignmentText }}
            </div>
          </div>
          
          <!-- フィードバック音声プレイヤー -->
          <div class="feedback-audio-section">
            <h4>🎤 講師からのフィードバック</h4>
            <div class="audio-player-container">
              <audio 
                v-if="feedbackData.signedUrl"
                :src="feedbackData.signedUrl"
                controls
                preload="metadata"
                class="feedback-player"
                controlslist="nodownload noplaybackrate"
                playsinline
                @play="startPlayback"
                @ended="endPlayback($event.target.currentTime)"
                @pause="endPlayback($event.target.currentTime)"
              >
                お使いのブラウザは音声再生に対応していません。
              </audio>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, computed, nextTick, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { supabase } from '../supabaseClient'
import { getApiBase } from '../utils/apiBase.js'
import { useSelectedLearner } from '../composables/useSelectedLearner.js'
import { ReliableRecorder } from '../utils/reliableRecorder.js'
import { useFeedbackCheck } from '../composables/useFeedbackCheck.js'
import LoadingModal from '../components/common/LoadingModal.vue'
import { todayJST } from '../composables/officialHolidays.js'

const route = useRoute()
const router = useRouter()

// 状態管理
const isLoading = ref(true) // ローディング状態
const isRecording = ref(false)
const recordingTime = ref(0)
const audioBlob = ref(null)
const audioUrl = ref('')
const recordingInterval = ref(null)
const displayTime = ref(0) // 表示用の時間（独立したカウンター）
const displayTimeInterval = ref(null) // 表示時間用のタイマー
const submissions = ref([])
const currentUser = ref(null)
const currentRecordingDay = ref(null)
const currentRecordingType = ref(null)
const mediaRecorder = ref(null) // MediaRecorderの参照を保持
const reliableRecorder = ref(null) // ReliableRecorderの参照を保持
const chunkCount = ref(0) // チャンク数を管理
const canStopRecording = ref(false) // 録音停止ボタンの有効/無効状態

// 自動リフレッシュ関連
let authSub = null
const refreshing = ref(false)
const today = ref(todayJST())

// 兄弟対応: 選択中の子（FB確認時に learnerId を渡す）
const { selectedLearnerId, loadLearners } = useSelectedLearner()

// GET /api/learner-membership-status — 提出可否はサーバー一元化
const membershipStatusLoaded = ref(false)
const membershipCanSubmitToday = ref(false)
const membershipMessage = ref('')

async function fetchMembershipStatus() {
  const lid = selectedLearnerId.value
  if (!lid) {
    membershipStatusLoaded.value = true
    membershipCanSubmitToday.value = false
    membershipMessage.value = '学習者が選択されていません。'
    return { can_access: false, info: null }
  }
  const { data: { session } } = await supabase.auth.getSession()
  const token = session?.access_token
  if (!token) {
    membershipStatusLoaded.value = true
    membershipCanSubmitToday.value = false
    membershipMessage.value = '会員状態を取得できませんでした。'
    return { can_access: false, info: null, failed: true }
  }
  const base = getApiBase()
  const url = `${base ? base + '/' : ''}api/learner-membership-status?learner_id=${encodeURIComponent(lid)}`
  const res = await fetch(url, {
    headers: { Authorization: `Bearer ${token}` }
  })
  membershipStatusLoaded.value = true
  if (!res.ok) {
    // 取得できなかった（通信・サーバーの不調）。未契約と扱わず、提出だけ止めて案内する（指示書 B）
    membershipCanSubmitToday.value = false
    membershipMessage.value = '会員状態を取得できませんでした。'
    return { can_access: false, info: null, failed: true }
  }
  const info = await res.json()
  membershipCanSubmitToday.value = !!info.can_submit_today
  membershipMessage.value = (info.message && String(info.message).trim()) || ''
  return { can_access: !!info.can_access, info }
}

const isRecordingStartDisabled = computed(() => {
  if (!membershipStatusLoaded.value) return true
  if (!membershipCanSubmitToday.value) return true
  return false
})

const recordingStartBlockedTitle = computed(() => {
  if (!membershipStatusLoaded.value) return '会員状態を確認中です'
  if (!membershipCanSubmitToday.value) {
    return membershipMessage.value?.trim() || '今日は録音・提出はできません'
  }
  return ''
})

// フィードバック確認機能
const {
  isChecking: isCheckingFeedback,
  showFeedbackModal,
  currentFeedbackType,
  feedbackData,
  playbackState,
  checkFeedback,
  startPlayback,
  endPlayback,
  closeFeedbackModal
} = useFeedbackCheck()
const showUploadProgress = ref(false) // アップロード進捗表示の制御
const uploadProgress = ref({ total: 0, completed: 0 }) // アップロード進捗情報

// 本番用アップロード進捗表示
const showProductionUploadProgress = ref(false) // 本番用アップロード進捗表示
const productionUploadProgress = ref({ 
  total: 0,        // 総チャンク数
  completed: 0,    // 完了チャンク数
  percentage: 0    // 進捗率（0-100）
})
const uploadStatus = ref('uploading') // 'uploading' | 'completed'
const currentRecordingId = ref(null) // 現在の録音ID

// 開発用設定（後で削除・無効化可能）
const ENABLE_UPLOAD_PROGRESS = false // これをfalseにすると進捗表示を無効化

// FB確認モーダル多重起動ガード
const feedbackModalOpenedOnce = ref(false)

// モーダル表示時のbodyクラス制御
watch(showProductionUploadProgress, (isVisible) => {
  if (isVisible) {
    document.body.classList.add('modal-open')
    // フェードインアニメーション用のクラスを追加
    nextTick(() => {
      const overlay = document.querySelector('.production-upload-progress-overlay')
      if (overlay) {
        overlay.classList.add('active')
      }
    })
  } else {
    document.body.classList.remove('modal-open')
  }
})

// 学習者切り替え時に会員状態を再取得
watch(selectedLearnerId, async () => {
  if (!currentUser.value) return
  try {
    const { can_access: canAccess, failed: membershipFailed } = await fetchMembershipStatus()
    // 取得できなかったときは未契約と扱わない（提出だけ止めて案内済み）。利用できないときは、Top で
    // 利用できる兄弟への切り替え、または（誰も利用できなければ）マイページを案内する（指示書 B）
    if (!membershipFailed && !canAccess) {
      router.push('/top')
    }
  } catch (e) {
    console.warn('会員状態の再取得に失敗:', e)
  }
})

// コンポーネントマウント時
onMounted(async () => {
  try {
    await loadUserData(true, false) // プロフィール取得をスキップ、認証監視は登録
    await loadLearners()
    try {
      const { can_access: canAccess, failed: membershipFailed } = await fetchMembershipStatus()
      // 取得できなかったときは未契約と扱わない（提出だけ止めて案内済み）。利用できないときは、Top で
      // 利用できる兄弟への切り替え、または（誰も利用できなければ）マイページを案内する（指示書 B）
      if (!membershipFailed && !canAccess) {
        router.push('/top')
        isLoading.value = false
        return
      }
    } catch (e) {
      console.warn('会員状態チェック中にエラーが発生しました:', e)
      membershipStatusLoaded.value = true
      membershipCanSubmitToday.value = false
      membershipMessage.value = '会員状態を取得できませんでした。'
    }

    // 依存関係がある処理は順次実行
    await loadSubmissions()  // currentUser.value に依存
    
    // 🆕 クエリパラメータチェック：FB確認モード
    const q = route.query
    const submissionId = Array.isArray(q.submissionId) ? q.submissionId[0] : q.submissionId
    const view = Array.isArray(q.view) ? q.view[0] : q.view
    
    if (view === 'feedback' && typeof submissionId === 'string' && submissionId) {
      // 多重起動ガード
      if (feedbackModalOpenedOnce.value) {
        console.log('⚠️ FB確認モーダル多重起動防止')
        return
      }
      feedbackModalOpenedOnce.value = true
      
      console.log('📝 FB確認モード検知:', { submissionId, view })
      await openFeedbackViewModal(submissionId)
    }
    
    // スクロール完了後にローディングモーダルを終了
    await performProgressScroll()
    isLoading.value = false
    
    // 自動リフレッシュ機能を開始
    document.addEventListener('visibilitychange', handleVisibilityChange)
    window.addEventListener('pageshow', handlePageShow) // iOS/Safariのbfcache対応
    // scheduleMidnightReload() // ← 深夜0時自動更新をOFF
    
  } catch (error) {
    console.error('初期化エラー:', error)
    isLoading.value = false
  }
})

// コンポーネントアンマウント時
onUnmounted(() => {
  // モーダル表示中の場合はbodyクラスをクリーンアップ
  document.body.classList.remove('modal-open')
  
  // 自動リフレッシュ機能のクリーンアップ
  document.removeEventListener('visibilitychange', handleVisibilityChange)
  window.removeEventListener('pageshow', handlePageShow)
  // 深夜0時関連のクリーンアップは不要（使用していないため）
  authSub?.unsubscribe?.()
  
  // 高信頼性録音器のクリーンアップ
  if (reliableRecorder.value) {
    reliableRecorder.value.stopRecording()
    reliableRecorder.value = null
  }
  
  // 既存のMediaRecorderクリーンアップ
  if (mediaRecorder.value) {
    mediaRecorder.value.stop()
    mediaRecorder.value = null
  }
  
  // タイマーのクリーンアップ
  if (recordingInterval.value) {
    clearInterval(recordingInterval.value)
    recordingInterval.value = null
  }
  
  // 表示時間カウンターのクリーンアップ
  stopDisplayTimeCounter()
})

// ユーザーデータ読み込み
const loadUserData = async (skipProfile = false, skipAuthWatch = false) => {
  try {
    console.log('🔍 認証データ初期化開始')
    
    // 1. 認証状態を確認
    const { data: { user }, error: authError } = await supabase.auth.getUser()
    
    if (authError) {
      console.error('認証エラー:', authError)
      return
    }
    
    if (!user) {
      console.log('⚠️ 認証されていないユーザーです')
      return
    }
    
    console.log('🔍 認証ユーザー:', user)
    currentUser.value = user
    
    // 2. プロフィール情報を取得（APIサーバー経由）- 再読み込み時はスキップ
    if (!skipProfile) {
      try {
        const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || 'https://api.otoeigo.com'
        const session = await supabase.auth.getSession()
        const accessToken = session.data.session?.access_token
        
        const response = await fetch(`${apiBaseUrl}/api/user-profile`, {
          headers: {
            'Authorization': `Bearer ${accessToken}`
          }
        })
        
        console.log('🔍 プロフィールAPI結果:', response.status)
        
        if (response.ok) {
          const result = await response.json()
          if (result.success && result.data) {
            console.log('✅ プロフィール設定完了:', result.data)
          } else {
            console.warn('⚠️ プロフィールAPI: データなし')
          }
        } else {
          console.warn('⚠️ プロフィールAPI失敗:', response.status)
        }
      } catch (error) {
        console.warn('⚠️ プロフィール取得をスキップします:', error.message)
      }
    } else {
      console.log('🚀 プロフィール取得をスキップ（再読み込み最適化）')
    }
    
    // 3. 認証状態の監視を開始（初回マウント時のみ）
    if (!skipAuthWatch) {
      const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
        console.log('🔍 認証状態変化:', event, session?.user?.id)
        if (event === 'SIGNED_IN' && session?.user) {
          currentUser.value = session.user
        } else if (event === 'SIGNED_OUT') {
          currentUser.value = null
        }
      })
      authSub = subscription
    } else {
      console.log('🚀 認証状態監視をスキップ（既に設定済み）')
    }
    
    console.log('✅ 認証データ初期化完了')
    
  } catch (error) {
    console.error('ユーザー情報取得エラー:', error)
  }
}

// 提出データ読み込み
const loadSubmissions = async () => {
  console.log('📥 loadSubmissions開始:', { userId: currentUser.value?.id })
  if (!currentUser.value) {
    console.log('⚠️ ユーザー情報なし、loadSubmissionsスキップ')
    return
  }
  
  try {
    console.log('📥 Supabaseから提出データ取得中...')
    // 🚀 最適化: 必要なカラムのみ取得（day, type, created_at）
    const { data, error } = await supabase
      .from('submissions_v3')
      .select('day, type, created_at')
      .eq('user_id', currentUser.value.id)
    
    if (error) {
      console.error('Supabaseエラー:', error)
      throw error
    }
    
    // データを手動でソート
    const sortedData = data ? [...data].sort((a, b) => a.day - b.day) : []
    submissions.value = sortedData
    console.log('✅ 提出データ取得完了:', { count: sortedData.length })
    
  } catch (error) {
    console.error('提出データ読み込みエラー:', error)
  }
}

// 進捗スクロール実行
const performProgressScroll = async () => {
  // 🚀 最適化: DOM更新を待ってからスクロール（500ms待機を削除）
  await nextTick()
  
  const nextIncompleteDay = findNextIncompleteDay()
  if (nextIncompleteDay) {
    scrollToDay(nextIncompleteDay, true) // true = 高速スクロール
    
    // スクロール完了を待つ（高速スクロールなので短時間で完了）
    await new Promise(resolve => setTimeout(resolve, 100))
  }
}

// 次の未完了日を探す
const findNextIncompleteDay = () => {
  // 🚀 最適化: 提出データがソート済みなので、最後の完了日を記録してそこから開始
  if (submissions.value.length > 0) {
    // 最後の提出日の次の日から開始（最大180日まで）
    const lastSubmittedDay = Math.max(...submissions.value.map(s => s.day))
    if (Number.isFinite(lastSubmittedDay) && lastSubmittedDay > 0) {
      const startDay = Math.min(lastSubmittedDay + 1, 180)
      
      // 最後の提出日の次の日から検索
      for (let day = startDay; day <= 180; day++) {
        if (!isDayCompleted(day)) {
          return day
        }
      }
      
      // 最後の提出日より前も検索（未提出の日がある可能性）
      for (let day = 1; day < startDay; day++) {
        if (!isDayCompleted(day)) {
          return day
        }
      }
    }
  }
  
  // 提出データがない場合、または無効な値の場合は最初から検索
  for (let day = 1; day <= 180; day++) {
    if (!isDayCompleted(day)) {
      return day
    }
  }
  
  return 180 // 全て完了している場合は最後の日
}

// コンテナ基準でスクロールする関数
function scrollContainerTo(el, container, behavior = 'smooth') {
  if (!el || !container) return

  // Day1の場合は上端に合わせる
  const dayNumber = parseInt(el.id.replace('day-', ''))
  if (dayNumber === 1) {
    container.scrollTo({ top: 0, behavior })
    return
  }

  // 要素の上端をコンテナの上端に合わせる（padding-top分考慮）
  const cRect = container.getBoundingClientRect()
  const eRect = el.getBoundingClientRect()
  const paddingTop = 20 // .days-container の padding-top
  
  const targetTop = Math.round(container.scrollTop + (eRect.top - cRect.top) - paddingTop)

  container.scrollTo({ top: Math.max(0, targetTop), behavior })

  // レイアウト変動対策の再調整
  if (behavior === 'smooth') {
    requestAnimationFrame(() => {
      const e2 = el.getBoundingClientRect()
      const c2 = container.getBoundingClientRect()
      const finalTop = Math.round(container.scrollTop + (e2.top - c2.top) - paddingTop)
      container.scrollTo({ top: Math.max(0, finalTop) })
    })
  }
}

// 指定した日までスクロール
const scrollToDay = (day, fastScroll = false) => {
  const el = document.getElementById(`day-${day}`)
  const container = document.querySelector('.days-container') // ← スクロール対象はコンテナ
  if (!el || !container) return

  scrollContainerTo(el, container, fastScroll ? 'auto' : 'smooth')
}

// 録音・提出前ガード（サーバー計算の can_submit_today のみ）
const guardHoliday = () => {
  if (!membershipStatusLoaded.value) {
    alert('会員状態を確認しています。しばらくしてからお試しください。')
    return false
  }
  if (membershipCanSubmitToday.value) return true
  alert(
    membershipMessage.value?.trim() ||
      '今日は録音・提出はできません。'
  )
  return false
}

// ソフトリロード（フル更新版）
const softReload = async () => {
  console.log('🔄 softReload開始（フル更新）:', { refreshing: refreshing.value })
  if (refreshing.value) {
    console.log('⚠️ 既にリフレッシュ中なのでスキップ')
    return
  }
  refreshing.value = true
  isLoading.value = true            // ← ローディングモーダルを表示
  console.log('📱 ローディングモーダル表示開始')
  try {
    console.log('画面復帰: 完全初期化を実行')
    today.value = todayJST()        // ← 今日を更新

    await loadUserData(true, true) // プロフィール取得と認証監視をスキップ
    await loadLearners()
    try {
      const { can_access: canAccess, failed: membershipFailed } = await fetchMembershipStatus()
      // 取得できなかったときは未契約と扱わない（提出だけ止めて案内済み）。利用できないときは、Top で
      // 利用できる兄弟への切り替え、または（誰も利用できなければ）マイページを案内する（指示書 B）
      if (!membershipFailed && !canAccess) {
        router.push('/top')
        return
      }
    } catch (e) {
      console.warn('会員状態チェック中にエラーが発生しました:', e)
      membershipStatusLoaded.value = true
      membershipCanSubmitToday.value = false
      membershipMessage.value = '会員状態を取得できませんでした。'
    }

    await loadSubmissions()  // currentUser.value に依存
    
    // クエリパラメータ処理
    const q = route.query
    const submissionId = Array.isArray(q.submissionId) ? q.submissionId[0] : q.submissionId
    const view = Array.isArray(q.view) ? q.view[0] : q.view
    
    if (view === 'feedback' && typeof submissionId === 'string' && submissionId) {
      if (feedbackModalOpenedOnce.value) {
        console.log('⚠️ FB確認モーダル多重起動防止')
        return
      }
      feedbackModalOpenedOnce.value = true
      console.log('📝 FB確認モード検知:', { submissionId, view })
      await openFeedbackViewModal(submissionId)
    }
    
    // スクロール処理
    await performProgressScroll()
    
    // 🚀 最適化: イベントリスナーの再登録は不要（既に登録済み）
    
    console.log('✅ フル更新完了')
  } finally {
    refreshing.value = false
    isLoading.value = false         // ← ローディングモーダルを非表示
    console.log('📱 ローディングモーダル表示終了')
  }
}

// 画面表示変更時の処理
const handleVisibilityChange = () => {
  console.log('🔍 visibilitychange検知:', { hidden: document.hidden })
  if (!document.hidden) {
    console.log('📱 画面復帰: softReload実行')
    softReload()     // 前面復帰で最新化
  }
}

// pageshowイベントハンドラー（iOS/Safariのbfcache対応）
const handlePageShow = (e) => { 
  if (e.persisted) {
    console.log('bfcache復帰: 録音データを再同期')
    softReload() 
  }
}

// 深夜0時自動更新は削除（画面復帰時の更新で十分）

// 練習録音開始
const startPracticeRecording = async (day) => {
  try {
    await fetchMembershipStatus()
  } catch (e) {
    console.warn('会員状態の再取得に失敗:', e)
  }
  if (!guardHoliday()) return
  await startRecording(day, 'practice')
}

// 課題録音開始
const startAssignmentRecording = async (day) => {
  try {
    await fetchMembershipStatus()
  } catch (e) {
    console.warn('会員状態の再取得に失敗:', e)
  }
  if (!guardHoliday()) return
  await startRecording(day, 'assignment')
}

// 録音開始
const startRecording = async (day, type) => {
  try {
    // 認証状態をチェック
    if (!currentUser.value) {
      console.error('❌ 認証されていないユーザーです')
      alert('ログインが必要です。ページを更新してください。')
      return
    }
    
    console.log('🎤 録音開始準備:', { day, type, userId: currentUser.value.id })
    
    // ReliableRecorder初期化
    reliableRecorder.value = new ReliableRecorder({
      onStart: () => {
        console.log('🎤 高信頼性録音開始:', { day, type })
        isRecording.value = true
        currentRecordingDay.value = day
        currentRecordingType.value = type
        recordingTime.value = 0
        chunkCount.value = 0
        canStopRecording.value = false // 録音停止ボタンを無効化
        
        // アップロード進捗表示をリセット・表示（開発用は無効化）
        uploadProgress.value = { total: 0, completed: 0 }
        if (ENABLE_UPLOAD_PROGRESS) {
          showUploadProgress.value = true
        }
        
        // 本番用進捗表示をリセット
        productionUploadProgress.value = { total: 0, completed: 0, percentage: 0 }
        uploadStatus.value = 'uploading'
        
        // 表示時間カウンターを開始（生徒に実感を与えるため）
        startDisplayTimeCounter()
        
        // 録音中のDayまでスクロール
        scrollToDay(day, false)
      },
      onProgress: (progress) => {
        recordingTime.value = Math.floor(progress.recordingTime / 1000)
        
        // アップロード進捗を更新
        uploadProgress.value = {
          total: progress.totalChunks || 0,
          completed: progress.uploadedChunks || 0
        }
        
        // チャンク数が1つ以上になったら録音停止ボタンを有効化
        if (progress.totalChunks >= 1 && !canStopRecording.value) {
          canStopRecording.value = true
          console.log('✅ チャンク作成完了、録音停止ボタンを有効化')
        }
      },
      onComplete: (result) => {
        console.log('✅ 高信頼性録音完了:', result)
        audioBlob.value = result.audioBlob // 既存のsubmitRecordingとの互換性のため
        isRecording.value = false
        
        // 表示時間カウンターを停止
        stopDisplayTimeCounter()
        
        // 即座にUIを更新（UX向上）— 会員チェックは録音開始前のみ
        handleImmediateSubmissionSuccess(result)
        
        // 提出処理完了後にリセット
        currentRecordingDay.value = null
        currentRecordingType.value = null
        canStopRecording.value = false // 録音停止ボタンを無効化
        chunkCount.value = 0
        if (ENABLE_UPLOAD_PROGRESS) {
          showUploadProgress.value = false // アップロード進捗表示を非表示
        }
      },
      onError: (error) => {
        console.error('❌ 高信頼性録音エラー:', error)
        isRecording.value = false
        currentRecordingDay.value = null
        currentRecordingType.value = null
        canStopRecording.value = false // 録音停止ボタンを無効化
        chunkCount.value = 0
        if (ENABLE_UPLOAD_PROGRESS) {
          showUploadProgress.value = false // アップロード進捗表示を非表示
        }
        
        // 表示時間カウンターを停止
        stopDisplayTimeCounter()
        
        // エラーハンドリング
        const errorMessage = error.message || error.toString()
        if (errorMessage.includes('Permission denied')) {
          alert('マイクへのアクセスが許可されていません')
        } else if (errorMessage.includes('NotSupportedError')) {
          alert('お使いのブラウザでは録音機能がサポートされていません')
        } else if (errorMessage.includes('DataCloneError')) {
          alert('録音データの保存中にエラーが発生しました。ページを再読み込みしてください。')
        } else {
          alert('録音中にエラーが発生しました: ' + errorMessage)
        }
      }
    })
    
    // 高信頼性録音開始
    await reliableRecorder.value.startRecording({
      day,
      type,
      userId: currentUser.value.id
    })
    
  } catch (error) {
    console.error('録音開始エラー:', error)
    alert('マイクへのアクセスが許可されていません')
  }
}

// 録音停止
const stopRecording = () => {
  console.log('⏹️ 録音停止ボタン押下')
  
  // 1. 即座にUIを更新（Vue.jsの状態を先に更新）
  isRecording.value = false
  stopDisplayTimeCounter()
  
  // 2. 本番用アップロード進捗表示を開始
  startProductionUploadProgress()
  
  // 3. 非同期で録音停止処理を実行
  setTimeout(async () => {
    try {
      console.log('🔄 録音停止処理開始')
      
      if (reliableRecorder.value) {
        await reliableRecorder.value.stopRecording()
      }
      // 既存のMediaRecorder対応（後方互換性）
      if (mediaRecorder.value) {
        mediaRecorder.value.stop()
      }
      if (recordingInterval.value) {
        clearInterval(recordingInterval.value)
        recordingInterval.value = null
      }
      
      console.log('✅ 録音停止処理完了')
      
    } catch (error) {
      console.error('❌ 録音停止エラー:', error)
    }
  }, 0)
}

// 録音提出
const submitRecording = async () => {
  try {
    // 高信頼性録音の場合は、ReliableRecorderが自動的にアップロードを処理
    if (reliableRecorder.value) {
      // 即座に完了（チャンクは既にアップロード済み）
      // 日本時間でタイムスタンプ生成
      const now = new Date()
      const jstTime = new Date(now.getTime() + (9 * 60 * 60 * 1000)) // UTC+9時間
      const timestamp = jstTime.toISOString().replace(/[:.]/g, '').slice(0, 15)
      
      const result = {
        success: true,
        submissionId: Date.now(),
        // 実ファイルパスは worker 側で最終決定されるため、固定キーを推測しない
        audioUrl: null
      }
      
      return handleSubmissionSuccess(result)
    }
    
    // 既存のアップロード処理（後方互換性）
    if (!currentUser.value || !currentRecordingDay.value || !currentRecordingType.value) {
      alert('録音データが不足しています')
      return
    }
    
    if (!audioBlob.value) {
      alert('録音データが不足しています')
      return
    }
    
    // ファイルサイズチェック（50MB制限）
    const maxSize = 50 * 1024 * 1024 // 50MB
    if (audioBlob.value.size > maxSize) {
      alert('ファイルサイズが大きすぎます（最大50MB）')
      return
    }
    
    // FormDataを作成
    const formData = new FormData()
    formData.append('audio', audioBlob.value, `recording_${currentRecordingType.value}_day${currentRecordingDay.value}.webm`)
    formData.append('day', currentRecordingDay.value.toString())
    formData.append('type', currentRecordingType.value)
    formData.append('duration', recordingTime.value.toString())
    formData.append('userId', currentUser.value.id)

    // バックエンドにアップロード
    const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || 'https://api.otoeigo.com'
    const response = await fetch(`${apiBaseUrl}/api/upload-audio`, {
      method: 'POST',
      body: formData
    })
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }
    
    const result = await response.json()
    
    if (result.success) {
      return handleSubmissionSuccess(result)
    } else {
      throw new Error(result.error || '提出に失敗しました')
    }
    
  } catch (error) {
    console.error('提出エラー:', error)
    
    if (error.message.includes('413')) {
      alert('ファイルサイズが大きすぎます')
    } else if (error.message.includes('415')) {
      alert('サポートされていないファイル形式です')
    } else {
      alert('提出に失敗しました: ' + error.message)
    }
  }
}

// 提出成功処理
// 即座にUIを更新する関数（UX向上）
const handleImmediateSubmissionSuccess = (result) => {
  // 提出可否の会員チェックは録音開始前（startPracticeRecording 等）のみ
  // 提出データをローカルに追加（即座に実行）
  const submission = {
    id: result.submissionId || Date.now(),
    user_id: currentUser.value.id,
    day: currentRecordingDay.value,
    type: currentRecordingType.value,
    status: 'submitted',
    audio_url: result.audioUrl,
    duration: recordingTime.value,
    created_at: new Date().toISOString()
  }
  
  submissions.value.push(submission)
  
  // フォームリセット
  audioBlob.value = null
  audioUrl.value = ''
  recordingTime.value = 0
  
  // 次の未完了日までスクロール
  const nextDay = findNextIncompleteDay()
  if (nextDay) {
    setTimeout(() => {
      scrollToDay(nextDay, false)
    }, 1000)
  }
}

// 非同期でバックエンド処理を実行
const submitRecordingAsync = async () => {
  try {
    await submitRecording()
  } catch (error) {
    console.error('❌ 非同期提出処理エラー:', error)
    // エラー時は既にUIが更新されているので、ユーザーには影響なし
  }
}

const handleSubmissionSuccess = (result) => {
  // 提出可否の会員チェックは録音開始前のみ
  // 提出データをローカルに追加
  const submission = {
    id: result.submissionId || Date.now(),
    user_id: currentUser.value.id,
    day: currentRecordingDay.value,
    type: currentRecordingType.value,
    status: 'submitted',
    audio_url: result.audioUrl,
    duration: recordingTime.value,
    created_at: new Date().toISOString()
  }
  
  submissions.value.push(submission)
  
  // フォームリセット
  audioBlob.value = null
  audioUrl.value = ''
  recordingTime.value = 0
  
  // 録音関連の状態をリセット
  currentRecordingDay.value = null
  currentRecordingType.value = null
  
  // 次の未完了日までスクロール
  const nextDay = findNextIncompleteDay()
  if (nextDay) {
    setTimeout(() => {
      scrollToDay(nextDay, false)
    }, 1000)
  }
}

// 録音破棄
const discardRecording = () => {
  audioBlob.value = null
  audioUrl.value = ''
  recordingTime.value = 0
}

// 表示時間カウンター開始
const startDisplayTimeCounter = () => {
  displayTime.value = 0
  displayTimeInterval.value = setInterval(() => {
    displayTime.value++
  }, 1000)
  console.log('⏰ 表示時間カウンター開始')
}

// 表示時間カウンター停止
const stopDisplayTimeCounter = () => {
  if (displayTimeInterval.value) {
    clearInterval(displayTimeInterval.value)
    displayTimeInterval.value = null
  }
  console.log('⏹️ 表示時間カウンター停止')
}

// 本番用アップロード進捗表示開始
const startProductionUploadProgress = async () => {
  try {
    // 1. 現在の録音IDを取得
    if (reliableRecorder.value && reliableRecorder.value.recordingId) {
      currentRecordingId.value = reliableRecorder.value.recordingId
      
      // 2. 既存のチャンク数を取得（IndexedDBから）
      const { IndexedDBStorage } = await import('../utils/indexedDBStorage.js')
      const storage = new IndexedDBStorage()
      const unuploadedChunks = await storage.getUnuploadedChunks()
      const currentRecordingChunks = unuploadedChunks.filter(
        chunk => chunk.recordingId === currentRecordingId.value
      )
      
      // 3. 進捗表示開始
      showProductionUploadProgress.value = true
      productionUploadProgress.value = {
        total: currentRecordingChunks.length,
        completed: 0,
        percentage: 0
      }
      uploadStatus.value = 'uploading'
      
      console.log('📤 本番用アップロード進捗表示開始:', {
        recordingId: currentRecordingId.value,
        totalChunks: currentRecordingChunks.length
      })
      
      // 4. アップロード監視開始
      startUploadMonitoring()
    }
  } catch (error) {
    console.error('❌ アップロード進捗表示開始エラー:', error)
  }
}

// アップロード監視
const startUploadMonitoring = () => {
  const checkInterval = 500 // 0.5秒間隔でチェック
  const maxWaitTime = 30000 // 30秒でタイムアウト
  let waitTime = 0
  
  const monitor = setInterval(async () => {
    try {
      // 現在のアップロード済みチャンク数を取得
      const { IndexedDBStorage } = await import('../utils/indexedDBStorage.js')
      const storage = new IndexedDBStorage()
      const unuploadedChunks = await storage.getUnuploadedChunks()
      const currentRecordingChunks = unuploadedChunks.filter(
        chunk => chunk.recordingId === currentRecordingId.value
      )
      
      const completedChunks = productionUploadProgress.value.total - currentRecordingChunks.length
      
      // 進捗を更新
      productionUploadProgress.value.completed = completedChunks
      productionUploadProgress.value.percentage = productionUploadProgress.value.total > 0 
        ? Math.round((completedChunks / productionUploadProgress.value.total) * 100) 
        : 0
      
      console.log('📊 アップロード進捗:', {
        completed: completedChunks,
        total: productionUploadProgress.value.total,
        percentage: productionUploadProgress.value.percentage
      })
      
      // アップロード完了チェック
      if (completedChunks >= productionUploadProgress.value.total) {
        clearInterval(monitor)
        await handleUploadComplete()
      }
      
      // タイムアウトチェック
      waitTime += checkInterval
      if (waitTime >= maxWaitTime) {
        clearInterval(monitor)
        console.warn('⚠️ アップロード監視タイムアウト')
        await handleUploadComplete() // タイムアウトでも完了扱い
      }
      
    } catch (error) {
      console.error('❌ アップロード監視エラー:', error)
      clearInterval(monitor)
    }
  }, checkInterval)
}

// アップロード完了処理
const handleUploadComplete = async () => {
  try {
    // 1. 進捗バーを100%に設定
    productionUploadProgress.value.percentage = 100
    productionUploadProgress.value.completed = productionUploadProgress.value.total
    
    // 2. 1秒待機（アニメーション）
    await new Promise(resolve => setTimeout(resolve, 1000))
    
    // 3. 完了メッセージ表示
    uploadStatus.value = 'completed'
    
    // 4. 2秒待機
    await new Promise(resolve => setTimeout(resolve, 2000))
    
    // 5. 自動消去
    showProductionUploadProgress.value = false
    uploadStatus.value = 'uploading'
    
    console.log('✅ アップロード進捗表示完了')
    
  } catch (error) {
    console.error('❌ アップロード完了処理エラー:', error)
  }
}

// FB確認モーダルを開く（ディープリンク用）
const openFeedbackViewModal = async (submissionId) => {
  try {
    console.log('📝 FB確認モーダルを開きます（submissionId指定）:', submissionId)
    
    // 🆕 submissionId を直接指定してモーダルを開く
    // 第3引数に true を渡して submissionId 指定モードに
    await checkFeedback(submissionId, null, true)
    
  } catch (error) {
    console.error('❌ FB確認モーダルエラー:', error)
    alert(`エラーが発生しました: ${error.message}`)
  }
}

// 時間フォーマット
const formatTime = (seconds) => {
  const mins = Math.floor(seconds / 60)
  const secs = seconds % 60
  return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
}

// 日付完了チェック
const isDayCompleted = (day) => {
  return isPracticeCompleted(day) && isAssignmentCompleted(day)
}

const isPracticeCompleted = (day) => {
  return submissions.value.some(s => s.day === day && s.type === 'practice')
}

const isAssignmentCompleted = (day) => {
  return submissions.value.some(s => s.day === day && s.type === 'assignment')
}

// 提出状況テキスト取得
const getPracticeStatusText = (day) => {
  const submission = submissions.value.find(s => s.day === day && s.type === 'practice')
  if (submission) {
    const date = new Date(submission.created_at)
    return `済（${date.toLocaleDateString('ja-JP')} ${date.toLocaleTimeString('ja-JP', {hour: '2-digit', minute: '2-digit'})}）`
  }
  return '未提出'
}

const getAssignmentStatusText = (day) => {
  const submission = submissions.value.find(s => s.day === day && s.type === 'assignment')
  if (submission) {
    const date = new Date(submission.created_at)
    return `済（${date.toLocaleDateString('ja-JP')} ${date.toLocaleTimeString('ja-JP', {hour: '2-digit', minute: '2-digit'})}）`
  }
  return '未提出'
}

// スタイルクラス取得
const getPracticeStatusClass = (day) => {
  return isPracticeCompleted(day) ? 'submitted' : 'not-submitted'
}

const getAssignmentStatusClass = (day) => {
  return isAssignmentCompleted(day) ? 'submitted' : 'not-submitted'
}




</script>

<style scoped>
.recording-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0.5rem 0.2rem;
}

.header {
  text-align: center;
  margin-bottom: 0.7rem;
}

.header h1 {
  color: #333;
  margin-bottom: 0.5rem;
  font-size: 2em;
  line-height: 0.2;
}



.subtitle {
  color: #666;
  font-size: 1.1rem;
  margin: 0;
  padding: 0;
}

/* 音声プレビュー */
.audio-preview {
  background: white;
  border-radius: 12px;
  padding: 2rem;
  margin-bottom: 2rem;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  text-align: center;
}

.audio-preview h4 {
  margin-bottom: 1.5rem;
  color: #333;
}

.audio-player {
  width: 100%;
  margin-bottom: 1.5rem;
}

.preview-actions {
  display: flex;
  gap: 1rem;
  justify-content: center;
}

.btn {
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: 1rem;
  font-weight: 600;
  transition: all 0.2s;
}

.btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-submit {
  background: #10b981;
  color: white;
}

.btn-submit:hover:not(:disabled) {
  background: #059669;
}

.btn-discard {
  background: #6b7280;
  color: white;
}

.btn-discard:hover:not(:disabled) {
  background: #4b5563;
}

/* 進捗一覧 */
.progress-list {
  background: white;
  border-radius: 12px;
  padding: 0.5rem;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

.progress-list h2 {
  text-align: center;
  margin-bottom: 0.2rem;
  color: #333;
}

.days-container {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.day-item {
  border: 2px solid #e1e5e9;
  border-radius: 12px;
  padding: 1.5rem;
  transition: all 0.2s;
}

.day-item:hover {
  border-color: #3b82f6;
  box-shadow: 0 4px 8px rgba(59, 130, 246, 0.15);
}

.day-item.current-recording {
  border-color: #f59e0b;
  background: #fef3c7;
  box-shadow: 0 4px 12px rgba(245, 158, 11, 0.3);
}

.day-item.completed {
  border-color: #10b981;
  background: #f0fdf4;
}

.day-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #e1e5e9;
}

.day-title {
  margin: 0;
  color: #333;
  font-size: 1.5rem;
  font-weight: bold;
}

/* 提出状況表示 */
.submission-status {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  text-align: right;
  font-size: 0.9rem;
}

.status-item {
  display: flex;
  align-items: center;
  gap: 0.25rem;
}

.status-label {
  font-weight: 600;
  color: #666;
}

.status-value {
  font-weight: 500;
}

.status-value.not-submitted {
  color: #dc2626;
}

.status-value.submitted {
  color: #10b981;
}

/* 録音セクション */
.recording-section {
  margin-bottom: 1.5rem;
  padding: 1rem;
  background: #f8fafc;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
}

.recording-section:last-child {
  margin-bottom: 0;
}

.recording-section.completed {
  background: #f0fdf4; /* ライトグリーン */
  border-color: #bbf7d0; /* グリーンのボーダー */
}

.section-title {
  margin: 0 0 1rem 0;
  color: #333;
  font-size: 1.1rem;
  font-weight: 600;
}

.completed-title {
  color: #6b7280;
}

.holiday-pill {
  display: inline-block;
  padding: 0.35rem 0.6rem;
  border-radius: 9999px;
  background: #e2e8f0;
  border: 1px solid #cbd5e1;
  font-weight: 700;
  color: #334155;
  margin-bottom: 1rem;
  text-align: center;
  width: 100%;
}

.recording-buttons {
  display: flex;
  gap: 1rem;
  align-items: center;
  justify-content: center;
  flex-wrap: nowrap;
  min-height: 60px; /* 録音中表示と同じ高さを確保 */
}

.btn-record-practice {
  background: #f59e0b;
  color: white;
  font-size: 1.1rem;
  padding: 0.75rem 1.5rem;
  white-space: nowrap;
  flex-shrink: 0;
}

.btn-record-practice:hover:not(:disabled) {
  background: #d97706;
}

.btn-record-assignment {
  background: #3b82f6;
  color: white;
  font-size: 1.1rem;
  padding: 0.75rem 1.5rem;
  white-space: nowrap;
  flex-shrink: 0;
}

.btn-record-assignment:hover:not(:disabled) {
  background: #2563eb;
}

.btn-stop-practice,
.btn-stop-assignment {
  background: #dc2626;
  color: white;
  font-size: 1.1rem;
  padding: 0.75rem 1.5rem;
}

.btn-stop-practice:hover,
.btn-stop-assignment:hover {
  background: #b91c1c;
}

.completed-status {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  background: #d1fae5;
  color: #065f46;
  border-radius: 8px;
  font-weight: bold;
  font-size: 1.1rem;
}

.completed-icon {
  font-size: 1.2rem;
}

.completed-text {
  color: #6b7280;
}

/* 録音中の表示 */
.recording-status-active {
  background: #fef3c7;
  border: 2px solid #f59e0b;
  border-radius: 12px;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
  text-align: center;
  /* 1段表示のためのflexレイアウト */
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 1rem;
  flex-wrap: nowrap;
}

.recording-indicator h4 {
  color: #92400e;
  margin: 0;
  white-space: nowrap;
}

.recording-time-display {
  margin: 0;
  white-space: nowrap;
}

.time-display {
  font-size: 2.5rem;
  font-weight: bold;
  color: #dc2626;
  font-family: monospace;
}

.btn-stop-recording {
  background: #dc2626;
  color: white;
  padding: 1rem 2rem;
  font-size: 1.2rem;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-weight: bold;
  transition: all 0.3s ease;
  white-space: nowrap;
  flex-shrink: 0;
}

.btn-stop-recording:hover:not(:disabled) {
  background: #b91c1c;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(220, 38, 38, 0.3);
}

.btn-stop-recording:disabled,
.btn-stop-recording.btn-disabled {
  background: #9ca3af;
  color: #6b7280;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

/* アップロード進捗ポップアップ（開発用） */
.upload-progress-popup {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.upload-progress-content {
  background: white;
  padding: 2rem;
  border-radius: 12px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
  min-width: 300px;
  text-align: center;
}

.upload-progress-content h3 {
  margin: 0 0 1rem 0;
  color: #1f2937;
  font-size: 1.2rem;
}

.progress-info {
  margin-bottom: 1rem;
}

.progress-info p {
  margin: 0.5rem 0;
  font-size: 1rem;
  color: #4b5563;
}

.progress-bar {
  width: 100%;
  height: 20px;
  background: #e5e7eb;
  border-radius: 10px;
  overflow: hidden;
  margin-bottom: 1rem;
}

.progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #3b82f6, #1d4ed8);
  transition: width 0.3s ease;
}

.btn-close {
  background: #6b7280;
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.9rem;
}

.btn-close:hover {
  background: #4b5563;
}

/* 本番用アップロード進捗表示 */
.production-upload-progress-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 10000;
  /* オーバーレイクリック防止 */
  pointer-events: auto;
  /* フェードアニメーション */
  opacity: 0;
  transition: opacity 0.3s ease;
}

.production-upload-progress-overlay.active {
  opacity: 1;
}

/* スクロール防止 */
body.modal-open {
  overflow: hidden;
}

.production-upload-progress-modal {
  background: white;
  padding: 3rem;
  border-radius: 16px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
  min-width: 400px;
  text-align: center;
  animation: slideIn 0.3s ease-out;
}

.upload-message h3 {
  margin: 0 0 0.5rem 0;
  color: #1f2937;
  font-size: 1.5rem;
  font-weight: bold;
}

.upload-message p {
  margin: 0 0 2rem 0;
  color: #6b7280;
  font-size: 1rem;
}

.progress-container {
  margin-bottom: 2rem;
}

.progress-bar {
  width: 100%;
  height: 24px;
  background: #e5e7eb;
  border-radius: 12px;
  overflow: hidden;
  margin-bottom: 1rem;
  box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.1);
}

.progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #3b82f6, #1d4ed8);
  transition: width 0.3s ease;
  border-radius: 12px;
  position: relative;
}

.progress-fill::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent);
  animation: shimmer 2s infinite;
}

.progress-text {
  color: #4b5563;
  font-size: 0.9rem;
  font-weight: 500;
}

.completion-message {
  color: #059669;
  font-size: 1.2rem;
  font-weight: bold;
  animation: fadeIn 0.5s ease-in;
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes shimmer {
  0% {
    transform: translateX(-100%);
  }
  100% {
    transform: translateX(100%);
  }
}

/* コンテナのスクロール設定 */
.days-container {
  max-height: 70vh;
  overflow-y: auto;
  scroll-snap-type: y mandatory;
  padding-top: 20px;
}

.day-item {
  scroll-snap-align: start;
  scroll-margin-top: 20px;
  margin-bottom: 20px;
}

/* day-title-section のスタイル */
.day-title-section {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  flex: 1;
}

/* FBを確認ボタンのスタイル */
.btn-feedback-check {
  padding: 0.5rem 1rem;
  background: #3b82f6;
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;
}

.btn-feedback-check:hover:not(:disabled) {
  background: #2563eb;
  transform: translateY(-1px);
}

.btn-feedback-check:disabled {
  background: #d1d5db;
  color: #9ca3af;
  cursor: not-allowed;
  transform: none;
}

/* フィードバックモーダルのスタイル */
.feedback-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  padding: 1rem;
}

.feedback-modal {
  background: white;
  border-radius: 12px;
  width: 100%;
  max-width: 600px;
  max-height: 80vh;
  overflow-y: auto;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem;
  border-bottom: 1px solid #e5e7eb;
}

.modal-header h3 {
  margin: 0;
  color: #1f2937;
  font-size: 1.25rem;
  font-weight: bold;
}

.btn-close {
  background: #ef4444;
  color: white;
  border: none;
  border-radius: 50%;
  width: 32px;
  height: 32px;
  font-size: 1.2rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}

.modal-content {
  padding: 1.5rem;
}

.assignment-section {
  margin-bottom: 2rem;
}

.assignment-section h4 {
  margin: 0 0 1rem 0;
  color: #374151;
  font-size: 1.1rem;
  font-weight: 600;
}

.assignment-text {
  background: #f8fafc;
  padding: 1rem;
  border-radius: 8px;
  border-left: 4px solid #3b82f6;
  line-height: 1.6;
  color: #374151;
}

.feedback-audio-section h4 {
  margin: 0 0 1rem 0;
  color: #374151;
  font-size: 1.1rem;
  font-weight: 600;
}

.audio-player-container {
  text-align: center;
}

.feedback-player {
  width: 100%;
  max-width: 400px;
  margin: 0 auto;
}

/* レスポンシブ対応 */
@media (max-width: 768px) {
  .day-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
  }
  
  .day-title-section {
    width: 100%;
  }
  
  .feedback-modal {
    margin: 0.5rem;
    max-height: 90vh;
  }
  
  .modal-header {
    padding: 1rem;
  }
  
  .modal-content {
    padding: 1rem;
  }
  
  .recording-buttons {
    flex-direction: row; /* モバイルでも横並びを維持 */
    gap: 0.5rem;
    min-height: 50px; /* モバイル用の高さ調整 */
  }
  
  .btn-record-practice,
  .btn-record-assignment {
    padding: 0.6rem 1.2rem;
    font-size: 1rem;
  }
  
  /* 録音中の1段表示をモバイルでも維持 */
  .recording-status-active {
    gap: 0.5rem;
    padding: 1rem;
  }
  
  .recording-indicator h4 {
    font-size: 0.9rem;
  }
  
  .time-display {
    font-size: 1.8rem;
  }
  
  .btn-stop-recording {
    padding: 0.8rem 1.5rem;
    font-size: 1rem;
  }
  
  .btn {
    width: 100%;
    text-align: center;
  }
}
</style>
